import ExcelJS from "exceljs";
import dayjs from "dayjs";
import { toAppointmentDate } from "./exportAdviserAppointmentsExcel";

export const toAdviserDobDate = (value) => {
  let timestamp;
  if (value instanceof Date) timestamp = value;
  else if (typeof value?.toDate === "function") timestamp = value.toDate();
  else if (value && typeof value === "object" && Number.isFinite(value.seconds ?? value._seconds)) {
    timestamp = new Date((value.seconds ?? value._seconds) * 1000);
  }
  if (!timestamp) return toAppointmentDate(value);
  if (Number.isNaN(timestamp.getTime())) return null;
  // Legacy DOB timestamps represent midnight in the UK, including BST.
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(timestamp);
  const part = (type) => parts.find((item) => item.type === type).value;
  return toAppointmentDate(`${part("year")}-${part("month")}-${part("day")}`);
};

export const getAdviserAge = (dob, asOf = new Date()) => {
  if (!dob || Number.isNaN(dob.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London", year: "numeric", month: "numeric", day: "numeric",
  }).formatToParts(asOf);
  const part = (type) => Number(parts.find((item) => item.type === type).value);
  const year = part("year"), month = part("month"), day = part("day");
  const birthYear = dob.getUTCFullYear(), birthMonth = dob.getUTCMonth() + 1, birthDay = dob.getUTCDate();
  const age = year - birthYear - Number(month < birthMonth || (month === birthMonth && day < birthDay));
  return age < 0 ? "" : age;
};

export async function exportAdviserDobAgeExcel(advisers = []) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Rosemount Staff Portal";
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet("Adviser DOB and Age", {
    views: [{ state: "frozen", ySplit: 1 }],
  });
  worksheet.columns = [
    { header: "ADVISER NAME", key: "name", width: 36 },
    { header: "APPOINTMENT STATUS", key: "appointmentStatus", width: 24 },
    { header: "DATE OF BIRTH", key: "dob", width: 20 },
    { header: "AGE", key: "age", width: 12 },
  ];

  const asOf = dayjs();
  advisers
    .map((adviser) => {
      const firstName = adviser?.preferredName || adviser?.firstName || "";
      const name = [firstName, adviser?.lastName].filter(Boolean).join(" ").trim();
      const dob = toAdviserDobDate(
        adviser?.dateOfBirth || adviser?.dob || adviser?.personalDetails?.dateOfBirth || adviser?.personalDetails?.dob,
      );
      return {
        name: name || adviser?.email || adviser?.id || "",
        appointmentStatus: adviser?.isAppointed === false ? "Unappointed / Leaver" : "Appointed",
        dob,
        age: getAdviserAge(dob, asOf.toDate()),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
    .forEach((row) => worksheet.addRow(row));

  worksheet.getColumn("dob").numFmt = "dd/mm/yyyy";
  worksheet.autoFilter = "A1:D1";
  worksheet.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
  worksheet.getRow(1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF081E56" },
  };
  worksheet.getRow(1).alignment = { vertical: "middle", horizontal: "center" };
  worksheet.getRow(1).border = {
    bottom: { style: "medium", color: { argb: "FF9EB25D" } },
  };
  worksheet.getRow(1).height = 26;
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber > 1 && rowNumber % 2 === 0) {
      row.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE9EBF8" },
      };
    }
  });

  const buffer = await workbook.xlsx.writeBuffer();
  return {
    blob: new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    filename: `Advisers DOB and Age - ${asOf.format("DD-MM-YYYY")}.xlsx`,
  };
}
